import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AuthProvider } from "react-oidc-context";
import { User, WebStorageStateStore } from "oidc-client-ts";
import * as Sentry from "@sentry/react";

import "./index.css";
import App from "./App.tsx";
import { LanguageProvider } from "./i18n";
import { AUTH_CONFIG } from "./config/constants";
import { IS_MOCK } from "./services/mock";

Sentry.init({
  dsn: "https://7431ec1375e3a6ce333033a16a44b791@o276488.ingest.us.sentry.io/4509958020530176",
  sendDefaultPii: false,
});

const cognitoAuthConfig = {
  // Mock mode never redirects, but the OIDC provider still constructs a
  // UserManager on mount -- give it harmless placeholders when env is unset.
  authority: AUTH_CONFIG.authority || (IS_MOCK ? "https://mock.invalid/" : ""),
  client_id: AUTH_CONFIG.clientId || (IS_MOCK ? "mock" : ""),
  redirect_uri:
    AUTH_CONFIG.redirectUri || (IS_MOCK ? window.location.origin : ""),
  post_logout_redirect_uri:
    AUTH_CONFIG.postLogoutRedirectUri ||
    (IS_MOCK ? `${window.location.origin}/logout-success` : ""),
  response_type: AUTH_CONFIG.responseType,
  scope: AUTH_CONFIG.scope,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Avoid background calls to a real Cognito authority in mock mode.
  automaticSilentRenew: !IS_MOCK,
  monitorSession: !IS_MOCK,
  onSigninCallback: (user: User | undefined) => {
    if (!user) {
      Sentry.captureMessage("No user information found after signin....");
      // Retry signin if state was lost
      window.location.reload();
      return;
    }

    // Remove the code from the URL to avoid issues with subsequent renders
    window.history.replaceState({}, document.title, window.location.pathname);
  },
  includeIdTokenInSilentRenew: true,
};

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <LanguageProvider>
      <AuthProvider {...cognitoAuthConfig}>
        <App />
      </AuthProvider>
    </LanguageProvider>
  </StrictMode>,
);
